import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { updateMachineModel, readAsyncStorageValues, getMachines } from "features/service/service.slice";
import {
  DialogTitle,
  Stack,
  Paper,
  Typography,
  DialogActions,
  Button,
  Dialog,
  Tooltip,
  DialogContent,
  useTheme,
  Box,
  IconButton,
  Grid,
} from "@mui/material";
import { toast } from "react-toastify";
import Header from "component/Header";
import CreateEditMachineModel from "pages/Assets/MachineModels/CreateEditMachineModel";
import AuthorizedImage from "component/AuthorizedImage";
import AuthorizedPDF from "component/AuthorizedPDF";
import videoImage from "images/Video.png";
import ViewImageVideoModal from "component/ViewImageVideoModal";
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  ChevronLeft as LeftArrowIcon,
  ChevronRight as RightArrowIcon,
  Delete as DeleteIcon,
  Upload,
} from "@mui/icons-material";
import ConfirmDeleteModal from "component/ConfirmDeleteModal";
import UploadProgressModal from "component/UploadProgressModal";
import BuildIcon from "@mui/icons-material/Build";
import { DataGrid } from "@mui/x-data-grid";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import TableChartIcon from "@mui/icons-material/TableChart";
import MovieIcon from "@mui/icons-material/Movie";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import TableSkeleton from "component/Loaders/TableSkeleton";

const MachineModel = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { machinesData, isLoading } = useSelector((state) => state.service);
  const { state } = useLocation();
  const { id } = useParams();
  const [initialFormData, setInitialFormData] = useState({
    model: "",
    info: { artifacts: [] },
  });
  const [enableEditing, setEnableEditing] = useState(false);
  const { appConstants } = useSelector((state) => state.service);
  // const { machinesList } = useSelector((state) => state.machines);
  const [showDialog, setShowDialog] = useState(false);
  const [showDialogView, setShowDialogView] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentFilePathIndex, setCurrentFilePathIndex] = useState(null);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ progress: 0, rate: 0, total: 0, buffer: 10 });
  const [isOpen, setIsOpen] = useState(false);

  // Utility to choose icon based on file type
  const getFileIcon = (filename) => {
    const ext = filename.split(".").pop().toLowerCase();
    switch (ext) {
      case "pdf":
        return <PictureAsPdfIcon color="primary" />;
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return <ImageIcon color="primary" />;
      case "doc":
      case "docx":
        return <DescriptionIcon color="action" />;
      case "xls":
      case "xlsx":
        return <TableChartIcon color="success" />;
      case "mp4":
      case "mov":
      case "avi":
        return <MovieIcon color="secondary" />;
      case "mp3":
      case "wav":
        return <AudiotrackIcon color="primary" />;
      default:
        return <InsertDriveFileIcon color="disabled" />;
    }
  };

  useEffect(() => {
    dispatch(getMachines());
  }, [dispatch]);

  useEffect(() => {
    if (id && appConstants) {
      const modelInfo = appConstants?.machineModels?.find((modelItem) => modelItem.id === parseInt(id));
      const info = modelInfo?.info ? JSON.parse(modelInfo?.info) : {};
      setInitialFormData({
        ...modelInfo,
        info: { ...info, artifacts: info.artifacts?.map((item) => ({ ...item, message: item.url })) || [] },
      });
    }
  }, [appConstants]);

  useEffect(() => {
    dispatch(readAsyncStorageValues());
    if (!id || state?.enableEditing) setEnableEditing(true);
  }, [dispatch, initialFormData.model]);

  const handleFileSelect = (event) => {
    setSelectedFiles([...event.target.files]);
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectedFiles([]);
    setShowDialog(false);
    setCurrentIndex(0);
    // fileInputRef.current.value = null;
  };
  const goToPrevSlide = () => setCurrentIndex(currentIndex > 0 ? currentIndex - 1 : 0);
  const goToNextSlide = () => setCurrentIndex(currentIndex === selectedFiles.length - 1 ? 0 : currentIndex + 1);

  const progressCallback = (progressEvent) => {
    const percentRateFraction = progressEvent.rate / 1048576;
    const percentTotalFraction = progressEvent.total / 1048576;
    const percentLoadedFraction = progressEvent.loaded / 1048576;
    const percentFraction = percentLoadedFraction / percentTotalFraction;
    const roundedNum = Math.round(percentLoadedFraction * 10) / 10;
    const roundedNumRate = Math.round(percentRateFraction * 10) / 10;
    const roundedNumTotal = Math.round(percentTotalFraction * 10) / 10;
    const diff = Math.random() * 10;
    setUploadProgress({ progress: roundedNum, rate: roundedNumRate, total: roundedNumTotal, buffer: percentFraction * 100 + diff + 5 });
  };

  const submitData = (data = initialFormData, action = null) => {
    const modelFormData = new FormData();
    const artifacts = [];
    const updateObj = {
      manufacturer: data.manufacturer,
      modelName: data.model,
      modelId: id,
      info: data?.info || {},
    };
    if (selectedFiles.length) {
      selectedFiles.forEach((item, index) => {
        modelFormData.append("image" + index, item);
        artifacts.push({
          name: item.name,
          type: item.type.includes("image/")
            ? appConstants.allTypes.TYPE_OF_CONTENT.IMAGE
            : item.type.includes("application/pdf")
            ? appConstants.allTypes.TYPE_OF_CONTENT.PDF
            : appConstants.allTypes.TYPE_OF_CONTENT.VIDEO,
          image: "image" + index,
        });
      });
      updateObj.artifacts = artifacts;
    }
    modelFormData.append("modelDetails", JSON.stringify(updateObj));
    setShowDialog(false);
    if (action !== "DELETE") setShowUploadDialog(true);
    dispatch(updateMachineModel({ modelFormData, progressCallback: progressCallback })).then((res) => {
      setSelectedFiles([]);
      if (res && res.error) toast.error("Something went wrong!");
      if (res && res.payload.data && res.payload.status === 200) {
        dispatch(readAsyncStorageValues()).then((resp) => {
          toast.success(`Artifact(s) ${id ? "updated" : "added"} successfully!`, {
            autoClose: 1000,
          });
          setUploadProgress({ progress: 0, rate: 0, total: 0, buffer: 10 });
          setShowUploadDialog(false);
          // props.setEnableEditing(false);
        });
      } else toast.error("Something went wrong!");
    });
  };
  const handleViewFile = (articfactIndex) => {
    setCurrentFilePathIndex(articfactIndex);
    setShowDialogView(true);
  };

  const renderLabel = (label, value) => (
    <Grid container spacing={1} mb={0.5} alignItems="center" wrap="nowrap">
      <Grid item sx={{ minWidth: 100 }}>
        <Typography fontWeight="bold" whiteSpace="nowrap">
          {label}
        </Typography>
      </Grid>
      <Grid item sx={{ minWidth: 20 }}>
        <Typography fontWeight="bold">:</Typography>
      </Grid>
      <Grid item xs>
        <Typography
          sx={{
            wordBreak: "break-word",
            whiteSpace: "normal",
          }}
        >
          {value || ""}
        </Typography>
      </Grid>
    </Grid>
  );

  const handleCloseDialogView = () => {
    setCurrentFilePathIndex(null);
    setShowDialogView(false);
  };

  const onClickDeleteArtifact = (artifact) => {
    const { artifacts } = initialFormData?.info;
    if (artifacts && initialFormData?.info) {
      setSelectedFile(artifact);
      setShowConfirmDeleteModal(true);
    }
  };
  const onConfirmDeleteArtifact = () => {
    const { artifacts } = initialFormData?.info;
    const newFormData = {
      ...initialFormData,
      info: { ...initialFormData?.info, artifacts: artifacts.filter((item) => item !== selectedFile) || [] },
    };
    setSelectedFile(null);
    setInitialFormData(newFormData);
    closeConfirmDeleteModal();
    submitData(newFormData, "DELETE");
  };
  const closeConfirmDeleteModal = () => setShowConfirmDeleteModal(false);
  const modelId = parseInt(id);
  const filteredMachines = machinesData.filter((machine) => machine.modelId === modelId);
  console.log(filteredMachines);
  const columns = [
    {
      field: "maintenanceCode",
      headerName: "Maintenance Code",
      width: 240, // Adjust as needed
    },
    {
      field: "serialNumber",
      headerName: "Serial Number",
      width: 180, // Adjust as needed
    },
  ];

  const rows =
    filteredMachines?.map((item, index) => ({
      id: item.id || index,
      maintenanceCode: item.maintenanceCode || "-",
      serialNumber: item.serialNumber || "-",
    })) || [];

  return (
    <>
      <Stack sx={{ ml: 2, mt: 2, mb: 1 }}>
        <Header titles={["Machine Model", id ? initialFormData.model : ""]} />
      </Stack>
      <div>
        <ConfirmDeleteModal
          showConfirmDeleteModal={showConfirmDeleteModal}
          closeConfirmDeleteModal={closeConfirmDeleteModal}
          modalTextContent={` Please press "Confirm Delete" to delete the selected item.`}
          onConfirmDelete={onConfirmDeleteArtifact}
        />
      </div>
      <div>
        <UploadProgressModal selectedFiles={selectedFiles} showUploadDialog={showUploadDialog} uploadProgress={uploadProgress} />
      </div>
      {
        <ViewImageVideoModal
          showDialogView={showDialogView}
          handleCloseDialogView={handleCloseDialogView}
          data={initialFormData?.info && initialFormData?.info?.artifacts ? initialFormData?.info?.artifacts : []}
          selectedFileIndex={currentFilePathIndex}
        />
      }
      <div>
        <Dialog open={showDialog} onClose={handleCloseDialog}>
          <DialogContent>
            {selectedFiles.length ? (
              selectedFiles[currentIndex].type.includes("image") ? (
                <img
                  src={URL.createObjectURL(selectedFiles[currentIndex])}
                  alt="Selected File"
                  style={{ maxWidth: "100%", maxHeight: "500px", minHeight: "200px" }}
                />
              ) : selectedFiles[currentIndex].type.includes("pdf") ? (
                <embed src={URL.createObjectURL(selectedFiles[currentIndex])} width="800" height="700" />
              ) : selectedFiles[currentIndex].type.includes("video") ? (
                <video controls style={{ width: "100%" }} height="auto">
                  <source src={URL.createObjectURL(selectedFiles[currentIndex])} type={selectedFiles[currentIndex].type} />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <span>Selected File is Incorrect</span>
              )
            ) : null}
          </DialogContent>
          <Stack direction={"row"} alignItems={"center"} justifyContent="center" sx={{ mt: 2, mb: 2, mx: 3, height: "100%" }}>
            <Button onClick={goToPrevSlide} sx={{ mr: 7 }}>
              <LeftArrowIcon />
              Prev
            </Button>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            {selectedFiles.length ? (
              <Button variant="contained" onClick={() => submitData()} sx={{ ml: 2, mr: 7 }}>
                Upload
              </Button>
            ) : null}
            <Button onClick={goToNextSlide}>
              Next <RightArrowIcon />
            </Button>
          </Stack>
        </Dialog>
      </div>
      <Box sx={{ mx: 2 }}>
        {enableEditing ? (
          <Paper sx={{ borderRadius: 2, mb: 1.5, p: 2 }}>
            <CreateEditMachineModel initialFormData={initialFormData} setEnableEditing={setEnableEditing} />
          </Paper>
        ) : (
          <Paper sx={{ borderRadius: 2, mb: 1.5, p: 2 }}>
            <Stack justifyContent={"space-between"} direction={"row"} flexWrap={"wrap"}>
            <Stack sx={{ flex: 2, mb: 1, gap: 0.5 }}>
              {renderLabel("Manufacturer ", initialFormData.manufacturer)}
              {renderLabel("Model ", initialFormData.model)}
            </Stack>
              <Stack rowGap={1}>
                <Tooltip title="Edit">
                  <Button onClick={() => setEnableEditing(true)} variant="contained">
                    Edit
                  </Button>
                </Tooltip>
              </Stack>
            </Stack>
          </Paper>
        )}
      </Box>

      {rows.length > 0 && (
        <Box sx={{ mx: 1.5 }}>
          {isLoading ? (
            <TableSkeleton rows={5} columns={columns.length} />
          ) : (
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={5}
              localeText={{ noRowsLabel: "No Data." }}
              sx={{
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "14px",
                  fontWeight: "bold",
                  backgroundColor: "white",
                },
                "& .MuiDataGrid-columnHeaders": {
                  minHeight: "40px !important",
                  backgroundColor: "white",
                },
                boxShadow: "0px 3px 15px rgba(0,0,0,0.1)",
                ml: 0.5, // equivalent to marginLeft: 5
                borderRadius: 2,
                overflow: "hidden", // ✅ important to make borderRadius visible
                backgroundColor: "white",
              }}
            />
          )}
        </Box>
      )}

      {id && (
        <Box sx={{ ml: 2, mt: 2, mb: 1, mr: 2 }}>
          <Paper sx={{ p: 2 }}>
            <Stack direction={"row"} justifyContent={"space-between"}>
              <Typography variant="h5" fontWeight={600} mb={1}>
                Artifacts
              </Typography>
              <input
                type="file"
                accept="application/pdf,video/*,image/*"
                style={{ display: "none" }}
                onChange={handleFileSelect}
                id="fileInput"
                multiple
              />
              <Tooltip title="Add file">
                <Button
                  sx={{ height: "self", marginBottom: "8px" }}
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setCurrentIndex(0);
                    document.getElementById("fileInput").click();
                  }}
                >
                  Add
                </Button>
              </Tooltip>
            </Stack>

            {initialFormData?.info && initialFormData?.info?.artifacts ? (
              <Stack direction="row" flexWrap="wrap" useFlexGap rowGap={2} columnGap={2} sx={{ pb: 1 }}>
                {initialFormData.info.artifacts.map((artifact, index) => (
                  <Paper
                    key={index}
                    sx={{
                      borderRadius: 2,
                      p: 1.5,
                      boxSizing: "border-box",
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      transition: "0.5s",
                      "&:hover": {
                        boxShadow: 6,
                      },
                    }}
                  >
                    <Stack rowGap={1} sx={{ cursor: "pointer" }} onClick={() => handleViewFile(index)}>
                      {/* Row container for name and delete icon */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                          gap: 1,
                        }}
                      >
                        {/* Icon + Truncated Filename */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            overflow: "hidden",
                            flexGrow: 1,
                          }}
                        >
                          {getFileIcon(artifact.name)}
                          <Box sx={{ maxWidth: 140, flexGrow: 1, overflow: "hidden" }}>
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              title={(() => {
                                const name = artifact.name || "";
                                const lastDotIndex = name.lastIndexOf(".");
                                if (lastDotIndex === -1)
                                  return name.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());

                                const base = name.slice(0, lastDotIndex);
                                const ext = name.slice(lastDotIndex);
                                const titleCasedBase = base.replace(
                                  /\w\S*/g,
                                  (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
                                );
                                return titleCasedBase + ext;
                              })()}
                              onClick={() => setIsOpen((prev) => !prev)}
                              sx={{
                                fontSize: "0.8rem",
                                cursor: "pointer",
                                textTransform: "none",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                flexGrow: 1,
                              }}
                            >
                              {(() => {
                                const name = artifact.name || "";
                                const lastDotIndex = name.lastIndexOf(".");
                                if (lastDotIndex === -1)
                                  return name.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());

                                const base = name.slice(0, lastDotIndex);
                                const ext = name.slice(lastDotIndex);
                                const titleCasedBase = base.replace(
                                  /\w\S*/g,
                                  (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
                                );
                                return titleCasedBase + ext;
                              })()}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Delete Icon */}
                        <IconButton
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation(); // prevent parent click
                            onClickDeleteArtifact(artifact);
                          }}
                          aria-label="Delete"
                          sx={{
                            padding: 0.5,
                            height: 28,
                            width: 28,
                            flexShrink: 0,
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            ) : null}
          </Paper>
        </Box>
      )}
    </>
  );
};

export default MachineModel;
